// ============================================================
// FILE LOCATION: src/components/TimelineBuilder/displayAdapter.ts
// ============================================================

/**
 * Display Adapter Utility
 * Derives clean display fields from raw timeline events
 */

import {
    TimelineEvent,
    EVENT_TYPE_OPTIONS,
    OUTCOME_OPTIONS,
  } from "./types";
  
  export interface DisplayEvent {
    id: string;
    displayTitle: string;
    displayDate: string;
    displaySnippet: string;
    badgeOutcome: { label: string; color: string } | null;
    badgeEvidenceCount: number;
    badgeImpactCount: number;
    hasWarning: boolean;
    raw: TimelineEvent;
  }
  
  /**
   * Get a clean display title from event
   */
  function getDisplayTitle(event: TimelineEvent): string {
    // If event has a custom title that's meaningful, use it
    if (event.title && event.title.trim() && event.title !== "Untitled event") {
      return event.title;
    }
  
    // Otherwise derive from event type
    const typeOption = EVENT_TYPE_OPTIONS.find(
      (opt) => opt.value === event.event_type
    );
    return typeOption?.label || "Event";
  }
  
  /**
   * Normalize date/duration into consistent display string
   */
  function getDisplayDate(event: TimelineEvent): string {
    // Priority: date_label > formatted start_date > approximate
    if (event.date_label) {
      return event.date_label;
    }
  
    if (event.start_date) {
      try {
        const date = new Date(event.start_date);
        if (!isNaN(date.getTime())) {
          // Format based on precision
          if (event.date_precision === "month") {
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          }
          if (event.date_precision === "year") {
            return date.getFullYear().toString();
          }
          // Default: short date
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        }
      } catch {
        // Fall through to default
      }
    }
  
    // Show duration if available
    if (event.start_date && event.end_date) {
      return `${event.start_date} – ${event.end_date}`;
    }
  
    return "Date TBD";
  }
  
  /**
   * Get truncated details snippet
   */
  function getDisplaySnippet(event: TimelineEvent, maxLength = 80): string {
    if (!event.details) return "";
  
    const cleaned = event.details.trim();
    if (cleaned.length <= maxLength) return cleaned;
  
    return cleaned.substring(0, maxLength).trim() + "…";
  }
  
  /**
   * Get outcome badge info
   */
  function getOutcomeBadge(
    event: TimelineEvent
  ): { label: string; color: string } | null {
    if (!event.outcome || event.outcome === "PENDING") return null;
  
    const option = OUTCOME_OPTIONS.find((o) => o.value === event.outcome);
    if (!option || !option.label) return null;
  
    const colorMap: Record<string, string> = {
      IMPROVED: "bg-emerald-100 text-emerald-700",
      PARTIAL: "bg-amber-100 text-amber-700",
      NO_CHANGE: "bg-slate-100 text-slate-600",
      WORSENED: "bg-red-100 text-red-700",
      INTOLERABLE: "bg-orange-100 text-orange-700",
    };
  
    return {
      label: option.label,
      color: colorMap[event.outcome] || "bg-slate-100 text-slate-600",
    };
  }
  
  /**
   * Transform raw event to display event
   */
  export function toDisplayEvent(event: TimelineEvent): DisplayEvent {
    return {
      id: event.id,
      displayTitle: getDisplayTitle(event),
      displayDate: getDisplayDate(event),
      displaySnippet: getDisplaySnippet(event),
      badgeOutcome: getOutcomeBadge(event),
      badgeEvidenceCount: event.evidence_flags?.length || 0,
      badgeImpactCount: event.function_tags?.length || 0,
      hasWarning: event.hasValidationWarning || false,
      raw: event,
    };
  }
  
  /**
   * Transform array of events
   */
  export function toDisplayEvents(events: TimelineEvent[]): DisplayEvent[] {
    return events.map(toDisplayEvent);
  }